/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import $ from '@/jquery'
import ext from '@/io.ox/core/extensions'
import api from '@/io.ox/core/folder/api'
import ModalDialog from '@/io.ox/backbone/views/modal'
import { getAPI } from '@/io.ox/oauth/keychain'

import gt from 'gettext'
import openSettings from '@/io.ox/settings/util'
import { createApp } from 'vue'
import Properties from '@/io.ox/core/folder/actions/properties.vue'

let oauthAPI
(async () => {
  oauthAPI = await getAPI()
})()

ext.point('io.ox/core/folder/actions/properties').extend({
  id: 'vue',
  index: 100,
  render () {
    try {
      const folderId = this.model.get('id')
      const app = createApp(Properties, { folderId })
      const $el = $('<div>')
      app.mount($el[0])
      this.$body.append($el)
      $el.on('dispose', { app }, e => e.data.app.unmount())
    } catch (err) {
      console.error(err)
    }
  }
})

ext.point('io.ox/core/folder/actions/properties').extend({
  id: 'account',
  index: 700,
  requires (model) {
    const provider = model.get('com.openexchange.calendar.provider')

    if (provider !== 'google') return false
    const config = model.get('com.openexchange.calendar.config')
    if (!config || !config.oauthId) return false
    const account = oauthAPI.accounts.get(config.oauthId)
    if (!account) return false
    const displayName = account.get('displayName')
    if (!displayName) return false
    return true
  },
  render () {
    const self = this
    const displayName = oauthAPI.accounts.get(this.model.get('com.openexchange.calendar.config').oauthId).get('displayName')
    this.$body.append(
      $('<div class="form-group">').append(
        $('<label>').text(gt('Account')),
        $('<div>').append(
          $('<a href="#" role="button">').text(displayName).on('click', function (e) {
            e.preventDefault()
            openSettings('virtual/settings/io.ox/settings/accounts')
            self.close()
          })
        )
      )
    )
  }
})

export default {
  openDialog: function folderProperties (id) {
    const model = api.pool.getModel(id)

    new ModalDialog({
      title: gt('Properties') + ': ' + model.getTitle(),
      point: 'io.ox/core/folder/actions/properties',
      model,
      width: 500,
      focus: '.btn-primary'
    })
      .addButton({ label: gt('Close'), action: 'close' })
      .open()
      .on('before:close', function () {
        // ModalView handles extensions of the referenced points in a specific way:
        // - When the dialog is opened, all extensions with unmet "requires" checks are disabled (`checkExtensions`).
        // - Once the dialog is closed, these extensions remain in their disabled state.
        // - If you switch to a different module, the still-enabled extensions might not pass the "requires" check anymore.
        // - If none of the remaining enabled extensions pass the "requires" check, the context menu does not show the "Properties" action, and only a refresh resolves this issue.
        //
        // Solution:
        // - Re-enable all extensions that were disabled by `checkExtensions`.
        // - Refer to issue #799 for a follow-up to replace this workaround with a real fix
        this.point.all().forEach(extension => this.point.toggle(extension.id, true))
      })
  }
}
